import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  resolveDocumentBreadcrumb,
  type FolderLike,
  type FolderPathSegment,
} from "../utils/folderPathResolver";

/** Lowercased filename → breadcrumb segments (null = filename only, no path). */
export type DocumentBreadcrumbMap = Map<string, FolderPathSegment[] | null>;

export interface DocRow {
  id: number;
  filename: string;
  project_id: number | null;
  folder_id: number | null;
  is_latest_version: boolean | null;
  is_dwg_child: boolean | null;
  /** True for rows in the hidden chatbot-attachments project (mirrored from
   *  `building_projects.hidden` by a DB trigger). */
  hidden: boolean | null;
}

/**
 * One row per filename, preferring the one whose path a user can actually see.
 *
 * Saving a chat file into a project **copies** it, so a saved file has two live
 * rows with the same name — the hidden original and the visible copy, each
 * `is_latest_version` in its own version group. Picking on `is_latest_version`
 * alone left the winner to whichever row PostgREST returned first.
 */
export function pickBreadcrumbRow(rows: DocRow[]): Map<string, DocRow> {
  const byFilename = new Map<string, DocRow>();
  for (const row of rows) {
    if (row.is_dwg_child) continue;
    const key = row.filename.toLowerCase();
    const existing = byFilename.get(key);
    if (!existing || compareRows(row, existing) > 0) {
      byFilename.set(key, row);
    }
  }
  return byFilename;
}

/**
 * Higher rank wins; equal ranks are broken by the higher id — the newest row.
 *
 * Rank alone leaves a genuine tie (the same filename in two different visible
 * projects, e.g. `report.pdf` uploaded to project A and a chat copy saved into
 * project B) resolved by PostgREST's row order, which is the nondeterminism
 * this function exists to remove: the chip's breadcrumb would point at one
 * project on one refetch and the other on the next. Neither is more correct,
 * so the tiebreak only has to be stable.
 */
function compareRows(a: DocRow, b: DocRow): number {
  return rank(a) - rank(b) || a.id - b.id;
}

/**
 * Visibility outranks version, always: a hidden row must never beat a visible
 * one, not even when the visible one is an older version. Comparing the two
 * flags as separate clauses let a hidden latest-version row displace a visible
 * non-latest one — reachable once the newest visible version is soft-deleted,
 * and it resolves the breadcrumb back to the hidden path this function exists
 * to keep off screen.
 */
function rank(row: DocRow): number {
  return (row.hidden ? 0 : 2) + (row.is_latest_version ? 1 : 0);
}

/**
 * Resolve interactive project → folder → filename breadcrumb segments for a
 * set of filenames in a building (WHA-3829: chatbot DocChip hover paths).
 *
 * Same resolution as `attachDocumentBreadcrumbs` (stores/pm/repository.ts),
 * but keyed by filename because chat chips only carry the filename string.
 * One batched query set per (building, filename-union); the union only grows
 * as the conversation does, so the key stays stable between messages.
 */
export function useDocumentBreadcrumbs(
  buildingId: string,
  filenames: string[]
): DocumentBreadcrumbMap | undefined {
  const sortedFilenames = useMemo(() => Array.from(new Set(filenames)).sort(), [filenames]);

  const { data } = useQuery({
    queryKey: ["document-breadcrumbs", buildingId, sortedFilenames],
    enabled: !!buildingId && Number.isFinite(Number(buildingId)) && sortedFilenames.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<DocumentBreadcrumbMap> => {
      const { data: docRows, error } = await supabase
        .from("documents")
        .select("id, filename, project_id, folder_id, is_latest_version, is_dwg_child, hidden")
        .eq("building_id", Number(buildingId))
        .eq("is_deleted", false)
        .in("filename", sortedFilenames);
      if (error) throw error;

      const docByFilename = pickBreadcrumbRow((docRows ?? []) as DocRow[]);

      const projectIds = Array.from(
        new Set(
          Array.from(docByFilename.values())
            .map((d) => d.project_id)
            .filter((id): id is number => id != null)
        )
      );

      const result: DocumentBreadcrumbMap = new Map();
      if (projectIds.length === 0) {
        docByFilename.forEach((_doc, key) => result.set(key, null));
        return result;
      }

      const [projectRes, folderRes] = await Promise.all([
        supabase.from("building_projects").select("id, name").in("id", projectIds),
        supabase
          .from("document_folders")
          .select("id, name, parent_id, folder_path, project_id")
          .in("project_id", projectIds)
          .eq("is_deleted", false),
      ]);
      if (projectRes.error) throw projectRes.error;
      if (folderRes.error) throw folderRes.error;

      const projects = new Map<string, { name: string }>(
        (projectRes.data ?? []).map((p) => [String(p.id), { name: p.name }])
      );
      const folders = new Map<string, FolderLike>(
        (folderRes.data ?? []).map((f) => [
          String(f.id),
          {
            folder_path: f.folder_path,
            name: f.name ?? undefined,
            project_id: f.project_id,
            parentId: f.parent_id != null ? String(f.parent_id) : null,
          },
        ])
      );

      docByFilename.forEach((doc, key) => {
        // A file that only exists in the hidden chatbot-attachments project
        // gets no path — its "franklin-chatbot-attachments > Created by
        // Franklin" breadcrumb named folders nobody can navigate to, and the
        // segments are clickable, so it led into a project the Documents page
        // filters out. The chip keeps its plain filename tooltip.
        //
        // This supersedes WHA-3774's `folder_id == null` rule, which used "has
        // no folder" as a proxy for "is a chat upload". That proxy also hid the
        // path of anything sitting at a project ROOT — including a chat file
        // the user has since saved to a real project, which is exactly the path
        // they want to see.
        result.set(
          key,
          doc.hidden
            ? null
            : resolveDocumentBreadcrumb(
                { project_id: doc.project_id, folder_id: doc.folder_id, filename: doc.filename },
                projects,
                folders
              )
        );
      });
      return result;
    },
  });

  return data;
}
